import { gql, useQuery } from '@apollo/client'
import * as d3 from 'd3-scale'
import { useMemo } from 'react'
import stringSimilarity from 'string-similarity'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import calc from '../../../utils/calc'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IPurchaseSummaryType {
  getCustomizedReport: {
    nodes: {
      reportResult: {
        tableData: {
          item: string
          category: string
          unit: string
          cost: number
          expected: number
          usageQty: number
          endQty: number
        }[]
      }
    }[]
  }
}

const query = gql`
  query LocationPurchaseSummary($iInputParams: JSON!) {
    getCustomizedReport(
      iCustomizedReportName: "LOCATION_PURCHASE_TABLE_V2"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

export const bbbLocationPurchaseConfigs = {
  itemName: 'string',
  displayParentCategoryName: 'string',
  unit: 'string',
  actualCogs: 'price',
  idealCogs: 'price',
  cogsVariance: 'price',
  usagePercent: 'percent',
  usageQty: 'number',
  endQty: 'number',
} as const

const buildBbbLocationPurchase = (specificItems?: string[]) => {
  const useBbbLocationPurchase = () => {
    const { groupFilter } = useGroupFilter()
    const dateFilter = useDateFilter()
    const { variables } = useVariables()
    const startDate = dateFilter.startDate
    const endDate = dateFilter.endDate
    const locationId = groupFilter?.ids?.[0] || 0
    const { data, loading } = useQuery<IPurchaseSummaryType>(query, {
      variables: {
        iInputParams: {
          locationId,
          startDate,
          endDate,
        },
      },
      skip: !startDate || !endDate || !groupFilter,
    })
    const bbbPurchaseCategories = specificItems
      ? 'All Categories'
      : variables.bbbPurchaseCategories?.value[0]?.[0]
    const bbbPurchaseCategoriesIsLoading =
      variables.bbbPurchaseCategories?.loading

    const formatedData = useMemo(() => {
      const tableData =
        data?.getCustomizedReport.nodes[0]?.reportResult.tableData

      if (data && !tableData) return []

      if (!tableData || !bbbPurchaseCategories) return null

      const formatedData = tableData.map((t) => ({
        ...t,
        id: t.item,
        parentId: 'root',
        itemName: t.item,
        displayParentCategoryName: t.category,
        unit: t.unit,
        actualCogs: t.cost,
        idealCogs: t.expected,
        cogsVariance: calc(t.cost, '-', t.expected) || 0,
        usageQty: t.usageQty,
        endQty: t.endQty,
      }))
      const newData =
        !bbbPurchaseCategories || bbbPurchaseCategories === 'All Categories'
          ? formatedData
          : formatedData.filter((f) => f.category === bbbPurchaseCategories)

      const minOpportunityCost = Math.min(
        ...newData.map((n) => n.cogsVariance as number),
      )
      const maxOpportunityCost = Math.max(
        ...newData.map((n) => n.cogsVariance as number),
      )
      const scaler = d3.scaleLinear(
        [0, 100],
        [minOpportunityCost, maxOpportunityCost],
      )

      return newData.map((n) => ({
        ...n,
        cogsVarianceHeatMap: scaler.invert(n.cogsVariance as number),
      }))
    }, [data, bbbPurchaseCategories])

    return {
      data: useMemo((): IApiDataType => {
        const searchInput = variables.inputValue?.itemSearch.value as
          | string
          | undefined

        if (specificItems)
          return (
            formatedData?.filter((f) => specificItems.includes(f.itemName)) ||
            null
          )

        if (!searchInput) return formatedData

        const searchPattern = searchInput
          .split(';')
          .map((search) => search.trim().toLowerCase())
          .filter(Boolean)
        const allItemNames = formatedData?.map((f) => f.item.toLowerCase())
        const matchItems = searchPattern.reduce((result, search) => {
          const bestMatchResults = stringSimilarity.findBestMatch(
            search,
            allItemNames || [],
          )
          const bestMatchRating = bestMatchResults.bestMatch.rating

          if (bestMatchRating < 0.5) return result

          const bestMatchItem = bestMatchResults.bestMatch.target

          return !bestMatchItem || result.includes(bestMatchItem)
            ? result
            : [...result, bestMatchItem]
        }, [] as string[])

        return (
          formatedData?.filter((n) => {
            const itemStr = n.item.toLowerCase()

            return (
              matchItems.includes(itemStr) ||
              searchPattern.some((s) => itemStr.includes(s))
            )
          }) || null
        )
      }, [formatedData, variables]),
      loading: loading || bbbPurchaseCategoriesIsLoading,
    }
  }

  return useBbbLocationPurchase
}

export const useBbbLocationPurchaseWithSpecificItems = buildBbbLocationPurchase(
  [
    'MEAT Korean BBQ Beef',
    'MEAT Chicken',
    'MEAT Tofu',
    'MEAT Salmon',
    'MEAT Steak',
    'MEAT Korean Crispy Chicken',
    'BEV BTL Water Sparkling Pineapple',
    'BEV BTL Water Sparkling Blackberry',
    'BEV BTL Honest Kids Apple',
    'BEV BTL Honest Kids Fruit Punch',
    'BEV BTL KOMBUCHA Ginger Lemon',
    'BEV BTL KOMBUCHA Tangerine Passion Fruit',
    'BEV BTL Water Sparkling Raspberry Lime',
    'BEV BTL Water',
    'GROC Bottle Sweet & Spicy Yum Yum',
    'GROC Bottle Sesame Ginger Teriyaki',
    'GROC Bottle Yum Yum',
    'PROD Avocado',
    'PROD Pineapple',
    'GROC Chocolate Cookie',
    'GROC Snickerdoodle Cookie',
  ],
)

const useBbbLocationPurchase = buildBbbLocationPurchase()

export default useBbbLocationPurchase
